import { ComponentChildren } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { Button } from '@bonniernews/dn-design-system-web/components/button/button.tsx'

export interface BuddyMenuProps extends SharedProps {
  /** Should be populated with product specific js */
  userName?: string
  /** Should be list item components */
  links?: ComponentChildren
  /** Should be populated with product specific js */
  accountLevel?: string
  /** Url for "Mitt konto" button */
  myAccountUrl: string
  /** Ex. [{ title: 'Annonsfri', href: "https://id.dn.se/konto/?nextUrl=/vanliga-fragor/dn/#Annonsfritt" }] */
  addons?: Array<{ title: string; href?: string; classNames?: string }>
  addonsClassNames?: string
  isDnSkola?: boolean
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/buddy-menu](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/buddy-menu)
 * - Storybook: [BuddyMenu](https://designsystem.dn.se/?path=/docs/page-buddymenu--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use "@bonniernews/dn-design-system-web/components/buddy-menu/buddy-menu" as *;`
 *
 * #### Adjust buddy menu position
 * These css variables can be overwritten to adjust the position of the buddy menu:
 *
 * ```css
 * --ds-buddy-menu-bottom-margin: 100px;
 * --ds-buddy-menu-position-top: 0;
 * --ds-buddy-menu-position-right: 0;
 * ```
 *
 * If we are using an (list-item) accordion in the buddy menu, we need to include the following js:
 *
 * ```javascript
 * import dsListItem from '@bonniernews/dn-design-system-web/components/list-item/list-item.js'
 * const listElements = Array.from(document.getElementsByClassName("ds-list-item"));
 * dsListItem(listElements);s
 * ```
 */
export const BuddyMenu = ({
  userName,
  links,
  accountLevel,
  myAccountUrl,
  addons,
  addonsClassNames,
  isDnSkola = false,
  classNames,
  attributes,
  forcePx,
}: BuddyMenuProps) => {
  const componentClassName = 'ds-buddy-menu'
  const classes = formatClassString([componentClassName, forcePx && 'ds-force-px', classNames])

  return (
    <div class={`${classes} ${isDnSkola && 'ds-buddy-menu--skola'}`} {...attributes}>
      <div class={`${componentClassName}__inner`}>
        <div class='ds-buddy-menu__scrollable-container'>
          <div class='ds-buddy-menu__content'>
            {
              <Button
                {...{
                  size: 'sm',
                  iconName: 'close',
                  classNames: 'ds-buddy-menu__close',
                  forcePx,
                  variant: 'transparent',
                  isIconButton: true,
                  attributes: {
                    'aria-label': 'stäng',
                  },
                }}
              />
            }

            <div class='ds-buddy-menu__header'>
              <span class='ds-buddy-menu__greeting'>
                Hej{' '}
                <span class='ds-buddy-menu__name js-buddy-menu-name' data-name-type='given'>
                  {userName}
                </span>
                !
              </span>
              {!isDnSkola && (
                <div class='ds-buddy-menu__account'>
                  <h2 class='ds-buddy-menu__account-title'>Min prenumeration</h2>
                  <span class='ds-buddy-menu__account-level js-buddy-menu__account-level'>{accountLevel}</span>

                  {addons && (
                    <div class={`ds-buddy-menu__addons ${addonsClassNames}`}>
                      <h2 class='ds-buddy-menu__addons-title'>Mina tillägg</h2>
                      <ul class='ds-buddy-menu__addons-list'>
                        {addons.map((addon) => (
                          <li class={addon.classNames}>
                            <a href={addon.href}>{addon.title}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button
                    {...{
                      text: 'Mitt konto',
                      size: 'lg',
                      variant: 'primaryBlack',
                      fullWidth: true,
                      href: myAccountUrl,
                      attributes: { 'data-hide-promo': '' },
                      forcePx,
                    }}
                  />
                </div>
              )}
            </div>

            <div class='ds-buddy-menu__links'>
              <h2 class='ds-buddy-menu__links-title'>Mitt innehåll</h2>
              <ul class='ds-buddy-menu__links-list'>{links}</ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
